import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, PROGRAM_LABELS, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const STATUS_COLORS = {
  paid: "bg-[#D4E4D3] text-[#2F4F2F]",
  pending: "bg-[#F9E8C0] text-[#7A5C1F]",
  failed: "bg-[#F2D6D3] text-[#8C3A3A]",
  refunded: "bg-[#E2E0DD] text-[#5C5955]",
};

export default function BookingsPage() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({ program:"", track:"", status:"", q:"" });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [journey, setJourney] = useState(null);

  const load = async () => {
    const params = {};
    Object.entries(filters).forEach(([k,v]) => { if (v) params[k]=v; });
    try {
      const { data } = await api.get("/admin/bookings", { params });
      setList(data);
    } catch(e){ toast.error(formatErr(e)); }
  };
  useEffect(()=>{ load(); }, [filters]);

  const openDetail = async (b) => {
    setSelected(b);
    setJourney(null);
    try { const { data } = await api.get(`/admin/bookings/${b.id}`); setDetail(data); } catch(e) {}
    if (b.program_type === "clarity_call") {
      try { const { data } = await api.get(`/admin/journeys/by-booking/${b.id}`); setJourney(data); } catch(e) {}
    }
  };

  const updateStatus = async (status) => {
    try {
      await api.patch(`/admin/bookings/${selected.id}`, { payment_status: status });
      toast.success(`Marked ${status}`);
      load(); openDetail(selected);
    } catch(e){ toast.error(formatErr(e)); }
  };

  const markClarityComplete = async () => {
    try {
      await api.patch(`/admin/bookings/${selected.id}`, { clarity_status: "completed" });
      toast.success("Clarity Call marked completed");
      openDetail(selected);
    } catch(e){ toast.error(formatErr(e)); }
  };

  const convertToJourney = async () => {
    try {
      const { data } = await api.post(`/admin/journeys/convert/${selected.id}`);
      setJourney(data);
      toast.success("iC One-to-One journey created");
    } catch(e){ toast.error(formatErr(e)); }
  };

  const exportCsv = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/export-bookings`, "_blank");
  };

  return (
    <div className="space-y-6 fade-up" data-testid="admin-bookings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-4xl">Bookings</h1>
          <p className="text-[#7E736E] text-sm">Master control of all registrations and payments.</p>
        </div>
        <button onClick={exportCsv} data-testid="export-bookings-btn" className="btn-ghost">Export CSV</button>
      </div>

      <div className="soft-card !p-5 grid sm:grid-cols-5 gap-3">
        <Input data-testid="bookings-search" placeholder="Search name / email / booking id" value={filters.q} onChange={e=>setFilters({...filters, q:e.target.value})}/>
        <Select value={filters.program} onValueChange={v=>setFilters({...filters, program:v==="all"?"":v})}>
          <SelectTrigger data-testid="filter-program"><SelectValue placeholder="Program"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All programs</SelectItem>
            {Object.entries(PROGRAM_LABELS).map(([k,v])=>(<SelectItem key={k} value={k}>{v}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={filters.track} onValueChange={v=>setFilters({...filters, track:v==="all"?"":v})}>
          <SelectTrigger data-testid="filter-track"><SelectValue placeholder="Track"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tracks</SelectItem>
            <SelectItem value="india">India</SelectItem>
            <SelectItem value="non_india">Non-India</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={v=>setFilters({...filters, status:v==="all"?"":v})}>
          <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            {["paid","pending","failed","refunded"].map(s=>(<SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
        <button onClick={()=>setFilters({program:"",track:"",status:"",q:""})} className="btn-ghost text-sm">Clear</button>
      </div>

      <div className="soft-card !p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="bookings-table">
          <thead className="bg-[#F3EFEA] text-xs uppercase tracking-wider text-[#7E736E]">
            <tr>
              <th className="text-left p-4">Booking</th>
              <th className="text-left">Program</th>
              <th className="text-left">Country</th>
              <th className="text-left">Amount</th>
              <th className="text-left">Status</th>
              <th className="text-left">Source</th>
              <th className="text-left">Registered</th>
            </tr>
          </thead>
          <tbody>
            {list.map(b => (
              <tr key={b.id} onClick={()=>openDetail(b)} className="border-t border-[#EBE5D9] cursor-pointer hover:bg-[#FDFBF7]" data-testid={`row-${b.id}`}>
                <td className="p-4">
                  <div className="font-medium">{b.full_name}</div>
                  <div className="text-xs text-[#7E736E]">{b.email}</div>
                </td>
                <td className="text-xs">{PROGRAM_LABELS[b.program_type] || b.program_type}</td>
                <td className="text-xs">{b.country} <span className="text-[#7E736E]">({b.track})</span></td>
                <td>{b.currency==="INR"?"₹":"$"}{b.amount}</td>
                <td><span className={`badge-soft ${STATUS_COLORS[b.payment_status]}`}>{b.payment_status}</span></td>
                <td className="text-xs">{b.source}</td>
                <td className="text-xs text-[#7E736E]">{(b.registered_at||"").slice(0,16).replace("T"," ")}</td>
              </tr>
            ))}
            {list.length === 0 && (<tr><td className="p-8 text-center text-[#7E736E]" colSpan={7}>No bookings yet.</td></tr>)}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o)=>{ if(!o){setSelected(null);setDetail(null);setJourney(null);}}}>
        <DialogContent className="sm:max-w-2xl" data-testid="booking-detail">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Booking Detail</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-[#7E736E] text-xs">Name</div><div>{detail.booking.full_name}</div></div>
                <div><div className="text-[#7E736E] text-xs">Email</div><div>{detail.booking.email}</div></div>
                <div><div className="text-[#7E736E] text-xs">Mobile</div><div>{detail.booking.country_code} {detail.booking.mobile}</div></div>
                <div><div className="text-[#7E736E] text-xs">Country</div><div>{detail.booking.country} ({detail.booking.track})</div></div>
                <div><div className="text-[#7E736E] text-xs">Program</div><div>{PROGRAM_LABELS[detail.booking.program_type]}</div></div>
                <div><div className="text-[#7E736E] text-xs">Slot</div><div>{detail.booking.slot_title} · {detail.booking.slot_datetime}</div></div>
                <div><div className="text-[#7E736E] text-xs">Amount</div><div>{detail.booking.currency==="INR"?"₹":"$"}{detail.booking.amount} ({detail.booking.payment_gateway})</div></div>
                <div><div className="text-[#7E736E] text-xs">Status</div><div><span className={`badge-soft ${STATUS_COLORS[detail.booking.payment_status]}`}>{detail.booking.payment_status}</span></div></div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={()=>updateStatus("paid")} data-testid="mark-paid" className="btn-primary text-xs">Mark Paid</button>
                <button onClick={()=>updateStatus("refunded")} className="btn-ghost text-xs">Mark Refunded</button>
                <button onClick={async()=>{await api.post(`/admin/bookings/${detail.booking.id}/resend-email`); toast.success("Email re-queued");}} className="btn-ghost text-xs">Resend Email</button>
              </div>
              {detail.booking.program_type === "clarity_call" && (
                <div className="rounded-xl border border-[#EBE5D9] bg-[#FBF8F3] p-4 space-y-3" data-testid="clarity-panel">
                  <div className="flex items-center justify-between">
                    <div className="font-serif text-lg">Clarity Call → iC One-to-One</div>
                    <span className="badge-soft bg-[#F3EFEA] text-[#5C5955]">{detail.booking.clarity_status || "scheduled"}</span>
                  </div>
                  {!journey ? (
                    <>
                      <p className="text-xs text-[#7E736E]">A 7-session journey begins once the Clarity Call is completed and you convert it.</p>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={markClarityComplete}
                          disabled={detail.booking.payment_status !== "paid" || detail.booking.clarity_status === "completed"}
                          className="btn-ghost text-xs disabled:opacity-50" data-testid="clarity-complete-btn">
                          Mark Clarity Call completed
                        </button>
                        <button onClick={convertToJourney}
                          disabled={!(detail.booking.payment_status === "paid" && detail.booking.clarity_status === "completed")}
                          className="btn-primary text-xs disabled:opacity-50" data-testid="convert-journey-btn">
                          Convert to iC One-to-One Journey
                        </button>
                      </div>
                      {detail.booking.payment_status !== "paid" && (
                        <p className="text-xs text-[#8C3A3A]">Capture payment first (Mark Paid) to enable conversion.</p>
                      )}
                    </>
                  ) : (
                    <div className="space-y-2" data-testid="journey-linked">
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 rounded-full bg-[#EBE5D9] overflow-hidden">
                          <div className="h-full bg-terracotta" style={{ width: `${(journey.completed_count / journey.total_sessions) * 100}%` }} />
                        </div>
                        <span className="text-xs text-[#7E736E]">{journey.completed_count}/{journey.total_sessions} sessions completed · {journey.status}</span>
                      </div>
                      <button onClick={()=>navigate("/admin/journeys")} className="btn-ghost text-xs" data-testid="open-journeys-btn">Manage in Journeys</button>
                    </div>
                  )}
                </div>
              )}
              <div>
                <div className="font-serif text-lg">Payment timeline</div>
                <ul className="mt-2 space-y-2">
                  {detail.payment_logs.map(l=>(
                    <li key={l.id} className="text-xs flex justify-between"><span>{l.event_type} · {l.message}</span><span className="text-[#7E736E]">{(l.created_at||"").slice(0,16).replace("T"," ")}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="font-serif text-lg">Email timeline</div>
                <ul className="mt-2 space-y-2">
                  {detail.email_logs.map(l=>(
                    <li key={l.id} className="text-xs flex justify-between"><span>{l.template_name} · {l.status}</span><span className="text-[#7E736E]">{(l.sent_at||l.created_at||"").slice(0,16).replace("T"," ")}</span></li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
